import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
  useTransition,
} from "react";
import { getLogger } from "@/utils/log";
import { getSystemColorScheme } from "@/utils/systemPreference";

const logger = getLogger("theme");

// Define available color schemes
export enum Theme {
  EMERALD = "emerald",
  AMBER = "amber",
  SKY = "sky",
  PURPLE = "purple",
}

// Define available color schemes (display modes)
export enum ColorScheme {
  DARK = "dark",
  LIGHT = "light",
  SYSTEM = "system",
}

// Theme context interface
interface ThemeContextType {
  // Theme = color palette (emerald, purple, etc.)
  theme: Theme;
  setTheme: (theme: Theme) => void;
  previewTheme: (theme: Theme | null) => void;
  previewThemeValue: Theme | null;

  // Color Scheme = light/dark/system display mode
  colorScheme: ColorScheme;
  setColorScheme: (scheme: ColorScheme) => void;
  toggleColorScheme: () => void;
  previewColorScheme: (scheme: ColorScheme | null) => void;
  previewColorSchemeValue: ColorScheme | null;

  // System preference handling
  isUsingSystemPreference: boolean;
  effectiveColorScheme: ColorScheme;

  // Transition state (React 19)
  isThemeChanging: boolean;
}

// Create the context with default values
const ThemeContext = createContext<ThemeContextType>({
  // Color scheme defaults (light/dark/system mode)
  colorScheme: ColorScheme.LIGHT,
  effectiveColorScheme: ColorScheme.LIGHT,
  // Transition state
  isThemeChanging: false,

  // System preference handling
  isUsingSystemPreference: false,
  previewColorScheme: () => {},
  previewColorSchemeValue: null,
  previewTheme: () => {},
  previewThemeValue: null,
  setColorScheme: () => {},
  setTheme: () => {},
  // Theme defaults (color palette)
  theme: Theme.EMERALD,
  toggleColorScheme: () => {},
});

// Props for the provider component
interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: Theme;
  initialColorScheme?: ColorScheme;
}

/**
 * Get the stored theme (color palette) from localStorage
 */
const getStoredTheme = (): Theme | null => {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem("theme-name");
  return (stored as Theme) || null;
};

/**
 * Get the stored color scheme (light/dark/system) from localStorage
 */
const getStoredColorScheme = (): ColorScheme | null => {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem("color-scheme");
  return (stored as ColorScheme) || null;
};

/**
 * Apply theme attributes to the HTML element
 *
 * The Tailwind plugin now uses data attributes for theming,
 * but we maintain the classes for backward compatibility
 */
const applyThemeAttributes = (theme: Theme, colorScheme: ColorScheme) => {
  if (typeof document === "undefined") return;

  const htmlElement = document.documentElement;

  // Set the theme data attribute for color palette (Tailwind theme plugin)
  htmlElement.setAttribute("data-theme", theme);

  // Set the color scheme data attribute for light/dark mode (Tailwind theme plugin)
  htmlElement.setAttribute("data-theme-mode", colorScheme);
};

/**
 * Theme provider component
 */
export const ThemeProvider = ({
  children,
  initialTheme = Theme.EMERALD,
  initialColorScheme,
}: ThemeProviderProps) => {
  // Initialize theme (color palette) state with stored values or defaults
  const [theme, setThemeState] = useState<Theme>(
    getStoredTheme() || initialTheme,
  );

  // Initialize color scheme (light/dark/system) state
  const storedColorScheme = getStoredColorScheme();
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(
    storedColorScheme || initialColorScheme || ColorScheme.SYSTEM,
  );

  // Track if using system preference
  const [isUsingSystemPreference, setIsUsingSystemPreference] =
    useState<boolean>(colorScheme === ColorScheme.SYSTEM || !storedColorScheme);

  // Keep track of the effective color scheme (what's actually applied)
  const [effectiveColorScheme, setEffectiveColorScheme] = useState<ColorScheme>(
    isUsingSystemPreference ? getSystemColorScheme() : colorScheme,
  );

  // Preview states
  const [previewThemeValue, setPreviewThemeValue] = useState<Theme | null>(
    null,
  );
  const [previewColorSchemeValue, setPreviewColorSchemeValue] =
    useState<ColorScheme | null>(null);

  // React 19: useTransition for non-blocking theme updates
  const [isThemeChanging, startTransition] = useTransition();

  // Update theme (color palette) and store in localStorage
  const setTheme = (newTheme: Theme) => {
    localStorage.setItem("theme-name", newTheme);
    startTransition(() => {
      setThemeState(newTheme);
    });
  };

  // Update color scheme (light/dark/system) and store in localStorage
  const setColorScheme = (scheme: ColorScheme) => {
    localStorage.setItem("color-scheme", scheme);
    startTransition(() => {
      setColorSchemeState(scheme);

      if (scheme === ColorScheme.SYSTEM) {
        setIsUsingSystemPreference(true);
        // Apply system preference immediately
        const systemPreference = getSystemColorScheme();
        setEffectiveColorScheme(systemPreference);
      } else {
        setIsUsingSystemPreference(false);
        setEffectiveColorScheme(scheme);
      }
    });
  };

  // Toggle between dark and light modes
  const toggleColorScheme = () => {
    // Clear any preview first to avoid race conditions
    setPreviewColorSchemeValue(null);

    // If currently in system mode, toggle to a specific mode
    if (colorScheme === ColorScheme.SYSTEM) {
      const newScheme =
        effectiveColorScheme === ColorScheme.DARK
          ? ColorScheme.LIGHT
          : ColorScheme.DARK;
      setColorScheme(newScheme);
      return;
    }

    // Otherwise toggle between light and dark
    const newScheme =
      colorScheme === ColorScheme.DARK ? ColorScheme.LIGHT : ColorScheme.DARK;
    setColorScheme(newScheme);
  };

  // Set a preview theme (when hovering)
  const previewTheme = (themeValue: Theme | null) => {
    setPreviewThemeValue(themeValue);
  };

  // Set a preview color scheme (when hovering)
  const previewColorScheme = (scheme: ColorScheme | null) => {
    // Set the preview value directly, including SYSTEM
    setPreviewColorSchemeValue(scheme);
  };

  // Apply theme attributes whenever theme or color scheme changes
  useEffect(() => {
    // If previewing, use the preview values, otherwise use the actual ones
    const activeTheme = previewThemeValue || theme;

    // Handle the case where preview is SYSTEM specifically
    let activeColorScheme: ColorScheme;
    if (previewColorSchemeValue === ColorScheme.SYSTEM) {
      activeColorScheme = getSystemColorScheme();
    } else if (previewColorSchemeValue) {
      activeColorScheme = previewColorSchemeValue;
    } else {
      activeColorScheme = isUsingSystemPreference
        ? effectiveColorScheme
        : colorScheme;
    }

    applyThemeAttributes(activeTheme, activeColorScheme);
  }, [
    theme,
    colorScheme,
    effectiveColorScheme,
    isUsingSystemPreference,
    previewThemeValue,
    previewColorSchemeValue,
  ]);

  // Listen for system preference changes
  useEffect(() => {
    // Skip if window or matchMedia is not available (like in some test environments)
    if (typeof window === "undefined" || !window.matchMedia) {
      logger.log(
        "window.matchMedia is not available, skipping color scheme listener",
      );
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = () => {
      // Only update if using system preference
      if (isUsingSystemPreference) {
        setEffectiveColorScheme(
          mediaQuery.matches ? ColorScheme.DARK : ColorScheme.LIGHT,
        );
      }
    };

    // Use modern addEventListener API
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [isUsingSystemPreference]);

  return (
    <ThemeContext
      value={{
        // Color scheme (light/dark/system)
        colorScheme,
        effectiveColorScheme,
        // Transition state (React 19)
        isThemeChanging,

        // System preference handling
        isUsingSystemPreference,
        previewColorScheme,
        previewColorSchemeValue,
        previewTheme,
        previewThemeValue,
        setColorScheme,
        setTheme,
        // Theme (color palette)
        theme,
        toggleColorScheme,
      }}
    >
      {children}
    </ThemeContext>
  );
};

/**
 * Custom hook to use the theme context
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
